import { Injectable } from "@angular/core";
import { StoreService } from "..";
import { catchError, from, map, Observable, tap } from "rxjs";
import { LLMInstructionsApiService } from "../../services/api/llm-instructions-api.service";
import { LLMInstruction, LLMInstructionBase, LLMInstructionCode, LLMInstructionsGroup } from "../../models/llm-instruction.model";

/**
 * How instruction code prefixes are grouped on the codes page. Group titles, remarks and prefix
 * names are translated from the codes (`content.llm.codes.groups.*`, `content.llm.prefixes.*`).
 */
export const LLM_INSTRUCTION_PREFIX_GROUPS: readonly LLMInstructionsGroup[] = [
  { code: 'role', prefixes: ['general_role'] },
  {
    code: 'general_instructions',
    prefixes: ['addressing_style', 'communication_style', 'subconscious_content', 'general_instructions'],
  },
  { code: 'placeholder_instructions', prefixes: ['placeholder_instructions'] },
  { code: 'output', prefixes: ['general_output', 'session_duration', 'elevenlabs_instructions'] },
];

/** Every prefix that has a translated name under `content.llm.prefixes`. */
export const LLM_INSTRUCTION_PREFIXES: readonly string[] = LLM_INSTRUCTION_PREFIX_GROUPS.flatMap((group) => group.prefixes);

@Injectable({
  providedIn: 'root'
})
export class LLMInstructionService {

  readonly PREFIX_GROUPS = LLM_INSTRUCTION_PREFIX_GROUPS;

  constructor(
    private store: StoreService,
    private llmInstructionsApiService: LLMInstructionsApiService
  ) { }

  get llmInstructions$(): Observable<LLMInstruction[]> {
    return this.store.state$.pipe(map(state => Object.values(state.llmInstructions)));
  }

  get llmInstructionCodes$(): Observable<LLMInstructionCode[]> {
    return this.store.state$.pipe(map(state => Object.values(state.llmInstructionCodes)));
  }


  loadCodes(): Observable<LLMInstructionCode[]> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.llmInstructionsApiService.getAllCodes()).pipe(
      tap((codes) => {
        const state = { ...this.store.state };
        codes.forEach((code) => state.llmInstructionCodes[code.code] = code);
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading LLM instruction codes:', error);
        throw error;
      })
    );
  }

  load(): Observable<LLMInstruction[]> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return this.llmInstructionsApiService.getAll().pipe(
      tap((instructions) => {
        const state = { ...this.store.state };
        instructions.forEach((instruction) => state.llmInstructions[instruction.id] = instruction);
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading LLM instructions:', error);
        throw error;
      })
    );
  }

  addLLMInstruction(instruction: LLMInstructionBase): Observable<LLMInstruction> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.llmInstructionsApiService.add(instruction)).pipe(
      tap((newInstruction) => {
        this.updateState(newInstruction);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error adding LLM instruction:', error);
        throw error;
      })
    );
  }

  updateLLMInstruction(instruction: LLMInstruction): Observable<LLMInstruction> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.llmInstructionsApiService.update(instruction)).pipe(
      tap((updatedInstruction) => {
        this.updateState(updatedInstruction);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error updating LLM instruction:', error);
        throw error;
      })
    );
  }

  private updateState(instruction: LLMInstruction) {
    const state = { ...this.store.state };
    state.llmInstructions[instruction.id] = instruction;
    state.loading = false;
    this.store.setState(state);
  }

  deleteLLMInstruction(id: number): Observable<void> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.llmInstructionsApiService.delete(id)).pipe(
      tap(() => {
        const state = { ...this.store.state };
        delete state.llmInstructions[id];
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error deleting LLM instruction:', error);
        throw error;
      })
    );
  }

  clear(): void {
    const state = { ...this.store.state };
    state.llmInstructions = {};
    state.llmInstructionCodes = {};
    this.store.setState(state);
  }
}
